import axios from 'axios';
import { getBaseURL, getApiHeaders } from '../config/api';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { API_CONFIG } from '../config/api';

export const useApi = () => {
    const createApiClient = () => {
        const baseURL = getBaseURL();
        const headers = getApiHeaders();
        
        console.debug(`🔗 useApi: Using base URL: ${baseURL}`);
        
        const api = axios.create({
            baseURL,
            headers,
            timeout: 30000
        });

        // Response interceptor voor automatische token refresh
        api.interceptors.response.use(
            response => response,
            async error => {
                const originalRequest = error.config;
                
                // Check of het een 401 error is en of we nog niet hebben geprobeerd te refreshen
                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;
                    
                    console.log('🔄 401 detected, attempting token refresh...');
                    
                    try {
                        const success = await refreshTokenIfNeeded();
                        if (success) {
                            // Update headers met nieuwe token
                            const newHeaders = getApiHeaders();
                            Object.assign(originalRequest.headers, newHeaders);
                            
                            console.log('✅ Token refreshed, retrying original request');
                            return api(originalRequest);
                        }
                    } catch (refreshError) {
                        console.error('Failed to refresh token:', refreshError);
                    }
                }
                
                return Promise.reject(error);
            }
        );

        return api;
    };

    const refreshTokenIfNeeded = async () => {
        try {
            const token = Cookies.get('auth_token');
            if (!token) {
                return false;
            }

            const response = await fetch(`${API_CONFIG.centralBackendURL}/api/v1/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                
                // Update token in cookie
                Cookies.set('auth_token', data.access_token, { 
                    expires: 1, // 1 dag
                    secure: window.location.protocol === 'https:',
                    sameSite: 'Strict'
                });

                return true;
            }
            
            return false;
        } catch (error) {
            console.error('Error in refreshTokenIfNeeded:', error);
            return false;
        }
    };

    const get = async (url, config = {}) => {
        const api = createApiClient();
        return api.get(url, config);
    };

    const post = async (url, data, config = {}) => {
        const api = createApiClient();
        return api.post(url, data, config);
    };

    const put = async (url, data, config = {}) => {
        const api = createApiClient();
        return api.put(url, data, config);
    };

    const del = async (url, config = {}) => {
        const api = createApiClient();
        return api.delete(url, config);
    };

    const checkHealth = async (detailed = false) => {
        try {
            const api = createApiClient();
            const endpoint = detailed ? '/health' : '/health/simple';
            const timeout = detailed ? 10000 : 5000; // Snellere timeout voor simple check
            
            const response = await api.get(endpoint, { timeout });
            return {
                isHealthy: response.data.status === 'healthy',
                status: response.data.status,
                data: response.data
            };
        } catch (error) {
            console.error(`Health check failed (${detailed ? 'detailed' : 'simple'}):`, error);
            return {
                isHealthy: false,
                status: 'unreachable',
                error: error.message
            };
        }
    };

    const checkHealthSimple = async () => {
        return checkHealth(false);
    };

    const checkHealthDetailed = async () => {
        return checkHealth(true);
    };

    return { 
        get, 
        post, 
        put, 
        delete: del, 
        checkHealth, 
        checkHealthSimple, 
        checkHealthDetailed 
    };
}; 